import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Radio } from 'lucide-react';
import { Eye, EyeOff } from 'lucide-react';
import { useLoginMutation } from '../../store/api';

interface LoginScreenProps {
  onLogin: (role: 'station' | 'superadmin', stationId: number | null | undefined, username: string) => void;
}

export function LoginScreen({ onLogin }: LoginScreenProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [login, { isLoading }] = useLoginMutation();

  const getLoginErrorMessage = (err: unknown) => {
    if (err && typeof err === 'object') {
      const maybeError = err as { status?: number | string; data?: unknown };
      if (maybeError.data && typeof maybeError.data === 'object') {
        const maybeData = maybeError.data as { detail?: unknown; message?: unknown };
        if (typeof maybeData.detail === 'string') {
          return maybeData.detail;
        }
        if (typeof maybeData.message === 'string') {
          return maybeData.message;
        }
      }
      if (typeof maybeError.data === 'string' && maybeError.data.trim().length > 0) {
        return maybeError.data;
      }
      if (maybeError.status) {
        return `Login failed (status ${maybeError.status}).`;
      }
    }

    return 'Invalid credentials. Please try again.';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Simple validation
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }

    try {
      const response = await login({
        username: username.trim(),
        password,
      }).unwrap();

      onLogin(response.role as 'station' | 'superadmin', response.stationId, response.username);
    } catch (err) {
      setError(getLoginErrorMessage(err));
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white border-gray-200">
        <CardHeader className="space-y-3 text-center">
          <div className="flex justify-center">
            <div className="size-16 bg-blue-600 rounded-full flex items-center justify-center">
              <Radio className="size-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl text-gray-900">Login</CardTitle>
          <CardDescription className="text-gray-600">
            Login credentials are provided by Admin.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username */}
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium text-gray-700">
                Username or Email
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="bg-white border-gray-300 text-gray-900"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-white border-gray-300 text-gray-900 pr-10"
                />
                {password.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
              disabled={isLoading}
            >
              {isLoading ? 'Logging In...' : 'Log In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
